import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { Cluster, Space, Stack } from 'component-library-next'

import { CustomerCategory, CustomersListFilterInput } from '@app/graphql/types'
import { useIntl } from '@app/intl/Intl'
import { ProductSelect } from '@app/lib/components/ProductSelect/ProductSelect'
import { ChoiceField } from '@app/lib/fields/ChoiceField/ChoiceField'
import { InputField } from '@app/lib/fields/InputField/InputField'
import { Button } from '@app/lib/inputs/Button/Button'
import { Radio } from '@app/lib/inputs/Radio/Radio'
import { TextInput } from '@app/lib/inputs/TextInput/TextInput'

export type CustomerFilterProps = {
  values: CustomersListFilterInput
  onSubmit: (values: CustomersListFilterInput) => void
  onReset: () => void
}

type FormData = {
  id: string
  productName: string
  customerCategory: CustomerCategory
}

export const CustomerFilterForm = ({ onSubmit, onReset, values }: CustomerFilterProps) => {
  const { formatMessage } = useIntl()

  const methods = useForm<FormData>({
    defaultValues: {
      id: values.id?.equals?.toString() || '',
      productName: values.productName?.equals || '',
      customerCategory: values.customerCategory?.equals ?? CustomerCategory.Active
    }
  })

  const handleSubmit = (values: FormData) => {
    onSubmit({
      ...(values.id
        ? {
            id: { equals: parseInt(values.id, 10) }
          }
        : { id: undefined }),
      ...(values.productName
        ? {
            productName: { equals: values.productName }
          }
        : { productName: undefined }),
      ...(values.customerCategory
        ? {
            customerCategory: { equals: values.customerCategory }
          }
        : { customerCategory: undefined })
    })
  }

  const handleReset = () => {
    methods.reset({})
    onReset()
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <Stack>
          <InputField
            name='id'
            label={formatMessage({ id: 'Lawyer.CustomerList.Filter.Id' })}
            render={({ inputProps }) => {
              return <TextInput {...inputProps} />
            }}
          />
          <InputField
            name='productName'
            label={formatMessage({ id: 'Lawyer.CustomerList.Filter.ProductName' })}
            render={({ inputProps }) => {
              return <ProductSelect {...inputProps} />
            }}
          />
          <ChoiceField
            name='customerCategory'
            label={formatMessage({ id: 'Lawyer.CustomerList.Filter.CustomerCategory' })}
            render={({ register }) => {
              return (
                <Stack gap={Space.tiny} role='radiogroup'>
                  <Radio {...register({ value: CustomerCategory.Active })}>
                    {formatMessage({
                      id: `Lawyer.CustomerList.Filter.CustomerCategory.ActiveMemberships`
                    })}
                  </Radio>
                  <Radio {...register({ value: CustomerCategory.Ended })}>
                    {formatMessage({
                      id: `Lawyer.CustomerList.Filter.CustomerCategory.EndedMemberships`
                    })}
                  </Radio>
                  <Radio {...register({ value: CustomerCategory.SingleProduct })}>
                    {formatMessage({
                      id: `Lawyer.CustomerList.Filter.CustomerCategory.SingleProductCustomers`
                    })}
                  </Radio>
                </Stack>
              )
            }}
          />
          <Cluster justification={Cluster.justification.right} gap={Cluster.gap.small}>
            <Button variant='outlined' color='neutral' type='reset' onClick={handleReset}>
              {formatMessage({ id: 'Lawyer.CustomerList.Filter.Btn.Reset' })}
            </Button>
            <Button variant='solid' color='primary' type='submit'>
              {formatMessage({ id: 'Lawyer.CustomerList.Filter.Btn.Filter' })}
            </Button>
          </Cluster>
        </Stack>
      </form>
    </FormProvider>
  )
}
